/*
 * TUI native bundle offer
 * ----------------------------------------------------------------------------
 * A <bundle-offer> custom element that lets a shopper pick variants for each
 * bundle slot, shows live discounted pricing, and adds the whole bundle in a
 * single /cart/add.js call. It deliberately reuses the theme's own cart
 * pipeline (cart:prepare-bundled-sections -> cart:change) so the cart drawer
 * opens and refreshes exactly like a normal add-to-cart. The real discount is
 * applied at cart/checkout by the tui-bundles discount Function; the numbers
 * here are display math only.
 *
 * The slot for the product the shopper already has open (data-current) follows
 * the main variant picker, so choosing "Raw Indigo / M" above does not have to
 * be repeated inside the card. Liquid already renders that slot on the selected
 * variant for the first paint and for any server re-render; this listener covers
 * the case where the theme swaps the variant without re-rendering product-info.
 */
(function () {
  if (window.customElements.get('bundle-offer')) return;

  const routeRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function formatMoney(cents) {
    return (
      '$' +
      (cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  class BundleOffer extends HTMLElement {
    connectedCallback() {
      this.percent = parseInt(this.dataset.percent || '0', 10) || 0;
      this.amountCents = parseInt(this.dataset.amountCents || '0', 10) || 0;
      this.totalCents = parseInt(this.dataset.totalCents || '0', 10) || 0;
      this.hasChoice = this.dataset.hasChoice === 'true';
      this.message = this.dataset.message || '';

      this.cta = this.querySelector('[data-bundle-add]');
      this.errorEl = this.querySelector('[data-bundle-error]');

      this.abortController = new AbortController();

      this.addEventListener('change', (event) => {
        if (event.target.matches('select[data-option]')) this.refresh();
      });

      // The theme dispatches variant:change on the product form; it bubbles to
      // the document. Anything unexpected in the payload is ignored rather than
      // thrown, because a failed sync must never take the Add button with it.
      document.addEventListener(
        'variant:change',
        (event) => {
          const variant = event && event.detail && event.detail.variant;
          if (variant) this.syncCurrentSlot(variant);
        },
        { signal: this.abortController.signal }
      );

      this.querySelectorAll('[data-choice-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this.selectChoice(tab.dataset.productId));
      });

      if (this.cta) {
        this.cta.addEventListener('click', () => this.addBundle());
      }

      // Mobile: the header is a toggle and the card starts collapsed (CSS hides the body
      // under 700px unless .is-open). Desktop always shows the whole card.
      this.toggle = this.querySelector('[data-bundle-toggle]');
      this.mobile = window.matchMedia('(max-width: 699px)');
      if (this.toggle) {
        this.toggle.addEventListener('click', () => {
          if (!this.mobile.matches) return;
          this.setOpen(!this.classList.contains('is-open'));
        });
        const syncExpanded = () =>
          this.toggle.setAttribute(
            'aria-expanded',
            !this.mobile.matches || this.classList.contains('is-open') ? 'true' : 'false'
          );
        this.syncExpanded = syncExpanded;
        this.mobile.addEventListener('change', syncExpanded, { signal: this.abortController.signal });
        syncExpanded();
      }

      this.refresh();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    /*
     * Mirror the main picker's variant into the slot for the product being
     * viewed. Only option values the select actually offers are applied, so a
     * variant from a different product can never strand the slot on a value
     * that has no matching variant.
     */
    syncCurrentSlot(variant) {
      const row = this.querySelector('[data-current="true"]');
      if (!row) return;

      const options =
        variant.options ||
        [variant.option1, variant.option2, variant.option3].filter((v) => v != null);
      if (!options || !options.length) return;

      let changed = false;
      row.querySelectorAll('select[data-option]').forEach((select) => {
        const position = parseInt(select.dataset.position, 10);
        const value = options[position - 1];
        if (value == null || select.value === value) return;
        const offered = Array.from(select.options).some((o) => o.value === value);
        if (!offered) return;
        select.value = value;
        changed = true;
      });

      if (changed) this.refresh();
    }

    setOpen(open) {
      this.classList.toggle('is-open', open);
      if (this.syncExpanded) this.syncExpanded();
    }

    /* All countable slots (each anchor + the choice slot, if any). */
    slots() {
      return Array.from(this.querySelectorAll('[data-slot]'));
    }

    /* The active row element (holding selects/variants/prices) for a slot. */
    activeRow(slot) {
      if (slot.dataset.role === 'choice') {
        const body = slot.querySelector('[data-choice-body]:not([hidden])');
        return body ? body.querySelector('[data-role="choice-inner"]') : null;
      }
      return slot;
    }

    variantsFor(row) {
      try {
        return JSON.parse(row.querySelector('[data-variants]').textContent);
      } catch (e) {
        return [];
      }
    }

    selectedVariant(row) {
      const variants = this.variantsFor(row);
      if (!variants.length) return null;
      const selects = row.querySelectorAll('select[data-option]');
      if (!selects.length) {
        return variants.find((v) => v.available) || variants[0];
      }
      const chosen = {};
      selects.forEach((s) => {
        chosen[parseInt(s.dataset.position, 10)] = s.value;
      });
      return (
        variants.find((v) =>
          Object.keys(chosen).every((pos) => v.options[pos - 1] === chosen[pos])
        ) || null
      );
    }

    discounted(cents, isDiscountLine) {
      if (this.percent > 0) {
        return cents - Math.floor((cents * this.percent) / 100);
      }
      if (this.amountCents > 0 && isDiscountLine) {
        return Math.max(0, cents - this.amountCents);
      }
      return cents;
    }

    selectChoice(productId) {
      this.querySelectorAll('[data-choice-tab]').forEach((tab) => {
        const selected = tab.dataset.productId === productId;
        tab.classList.toggle('is-selected', selected);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      });
      this.querySelectorAll('[data-choice-body]').forEach((body) => {
        body.hidden = body.dataset.productId !== productId;
      });
      this.refresh();
    }

    refresh() {
      let originalTotal = 0;
      let discountedTotal = 0;
      let available = true;
      this.items = [];

      this.slots().forEach((slot) => {
        const row = this.activeRow(slot);
        if (!row) {
          available = false;
          return;
        }
        const variant = this.selectedVariant(row);
        const priceEl = row.querySelector('[data-line-price]');
        const compareEl = row.querySelector('[data-line-compare]');

        if (!variant || variant.available === false) {
          available = false;
          if (priceEl && variant) {
            priceEl.textContent = formatMoney(variant.price);
            priceEl.classList.add('bundle-offer__price--full');
          }
          return;
        }

        const isDiscountLine = row.dataset.discountLine === 'true';
        const disc = this.discounted(variant.price, isDiscountLine);
        originalTotal += variant.price;
        discountedTotal += disc;
        this.items.push({ id: variant.id, quantity: 1 });

        // Lines always show their full price; the saving is shown once, on the set total.
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (compareEl) compareEl.hidden = true;
      });

      // Fixed bundle price: the whole bundle costs a flat total (rows show full price).
      if (this.totalCents > 0) {
        discountedTotal = Math.min(originalTotal, this.totalCents);
      }

      const save = originalTotal - discountedTotal;

      const totalPriceEls = this.querySelectorAll('[data-total-price]');
      const totalCompareEls = this.querySelectorAll('[data-total-compare]');
      const saveLineEl = this.querySelector('[data-save-line]');
      const saveAmountEl = this.querySelector('[data-save-amount]');
      const ctaSaveEl = this.querySelector('[data-cta-save]');
      const ctaTotalEl = this.querySelector('[data-cta-total]');
      const pillAmountEl = this.querySelector('[data-pill-amount]');

      // The footer total and the collapsed bar's mini total both carry these attributes.
      totalPriceEls.forEach((el) => {
        el.textContent = formatMoney(discountedTotal);
        el.classList.toggle('bundle-offer__total-price--full', save <= 0);
      });
      totalCompareEls.forEach((el) => {
        if (save > 0) {
          el.textContent = formatMoney(originalTotal);
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      });
      if (saveAmountEl) saveAmountEl.textContent = formatMoney(save);
      if (ctaSaveEl) ctaSaveEl.textContent = formatMoney(save);
      if (ctaTotalEl) ctaTotalEl.textContent = formatMoney(discountedTotal);
      if (pillAmountEl) pillAmountEl.textContent = formatMoney(save);
      if (saveLineEl) saveLineEl.hidden = true;

      this.setAvailable(available);
    }

    setAvailable(available) {
      if (!this.cta) return;
      this.cta.setAttribute('aria-disabled', available ? 'false' : 'true');
      if (this.errorEl) this.errorEl.hidden = available;
    }

    async addBundle() {
      if (!this.cta || this.cta.getAttribute('aria-disabled') === 'true') return;
      if (this.cta.getAttribute('aria-busy') === 'true') return;
      if (!this.items || !this.items.length) return;

      const settings = (window.themeVariables && window.themeVariables.settings) || {};
      this.cta.setAttribute('aria-busy', 'true');
      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:start', { bubbles: true })
      );

      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections },
        })
      );

      try {
        const response = await fetch(`${routeRoot()}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: this.items,
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        });
        const responseJson = await response.json();
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:loading:end', { bubbles: true })
        );

        if (response.ok) {
          if (settings.cartType === 'page' || settings.pageType === 'cart') {
            window.location.href = `${routeRoot()}cart`;
            return;
          }
          const cart = await (await fetch(`${routeRoot()}cart.js`)).json();
          cart.sections = responseJson.sections;
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: {
                baseEvent: 'variant:add',
                onSuccessDo: 'force_open_drawer',
                cart,
              },
            })
          );
        } else {
          if (this.errorEl) {
            this.errorEl.textContent =
              responseJson.description || 'Something went wrong. Please try again.';
            this.errorEl.hidden = false;
          }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } catch (e) {
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:loading:end', { bubbles: true })
        );
        if (this.errorEl) {
          this.errorEl.textContent = 'Something went wrong. Please try again.';
          this.errorEl.hidden = false;
        }
      } finally {
        this.cta.removeAttribute('aria-busy');
      }
    }
  }

  window.customElements.define('bundle-offer', BundleOffer);
})();
